/*
  Vehicle context helpers for the Year/Make/Model garage.

  Three jobs:

  1. Visit reset — the garage keeps the selected vehicle indefinitely, so a
     customer returning days later still browses through a filter they have
     forgotten about. Clear it when a visit starts: no page view or click in
     any tab for four hours. Tabs share the timestamp (localStorage), so a link
     opened in a new tab keeps the vehicle; sessionStorage, used before, starts
     empty in every new tab and cleared it. A URL that carries vehicle
     parameters (a shared or bookmarked filtered link) is always honoured.

  2. Collection notice — when a vehicle is selected, collection pages silently
     filter to it. Surface that with a one-click way out, and say so more
     plainly when the selected vehicle belongs to a different make than the
     collection being viewed.

  3. Header chip — a theme-owned button that opens the app's vehicle dialog,
     shows the saved vehicle once there is one, and clears it from the ×.
*/
(function () {
  var ACTIVITY_KEY = 'vehicle-last-active';
  // A visit ends after this long with no page view or click in any tab.
  var IDLE_LIMIT = 4 * 60 * 60 * 1000;
  var VEHICLE_PARAM = /(^|[?&/])(year|make|model|submodel|engine)\//i;
  var READY_INTERVAL = 100;
  var READY_ATTEMPTS = 200;

  function urlHasVehicle() {
    return VEHICLE_PARAM.test(window.location.search);
  }

  // Last time any tab of the store was used, or null when storage is blocked.
  function lastActivity() {
    try {
      return Number(window.localStorage.getItem(ACTIVITY_KEY)) || 0;
    } catch (error) {
      return null;
    }
  }

  function markActivity() {
    try {
      window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    } catch (error) {
      // Storage blocked: nothing to keep.
    }
  }

  function whenReady(callback) {
    var attempts = 0;

    (function poll() {
      var api = window.Convermax;
      if (api && typeof api.getVehicle === 'function') {
        callback(api);
        return;
      }
      attempts += 1;
      if (attempts > READY_ATTEMPTS) return;
      window.setTimeout(poll, READY_INTERVAL);
    })();
  }

  function hasVehicle(api) {
    return typeof api.isVehicleSelected === 'function' && api.isVehicleSelected();
  }

  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function vehicleMake(api) {
    var vehicle = typeof api.getVehicle === 'function' ? api.getVehicle() : null;
    if (!vehicle) return '';
    return vehicle.Make || vehicle.make || '';
  }

  function vehicleLabel(api) {
    if (typeof api.getVehicleString === 'function') {
      var label = api.getVehicleString();
      if (label) return label;
    }
    return vehicleMake(api);
  }

  /* The make this collection belongs to, or '' when it is not make-specific
     (system-type collections such as cat-back-exhausts). */
  function collectionMake(handle, makes) {
    for (var i = 0; i < makes.length; i++) {
      var make = makes[i];
      if (handle === make || handle.indexOf(make + '-') === 0) return make;
    }
    return '';
  }

  function makesMatch(collectionSlug, selectedMake) {
    var a = normalise(collectionSlug);
    var b = normalise(selectedMake);
    if (!a || !b) return true;
    return a.indexOf(b) === 0 || b.indexOf(a) === 0;
  }

  function sessionReset() {
    var last = lastActivity();
    markActivity();

    // Storage blocked (private mode, cookie settings): leave the garage alone.
    if (last === null) return;
    // Deep link into a filtered view: honour it.
    if (urlHasVehicle()) return;
    // Another page view, in this tab or any other, within the window: same visit.
    if (last && Date.now() - last < IDLE_LIMIT) return;

    whenReady(function (api) {
      if (hasVehicle(api) && typeof api.discardVehicle === 'function') {
        api.discardVehicle();
      }
    });
  }

  // Clicks and tab switches extend a visit that is still running (so a link opened in a new tab
  // from a page read for a while keeps the vehicle), but never revive one that has lapsed.
  function keepAlive() {
    var last = lastActivity();
    if (last && Date.now() - last < IDLE_LIMIT) markActivity();
  }

  document.addEventListener('pointerdown', keepAlive, true);
  document.addEventListener('visibilitychange', keepAlive);

  // A vehicle picked in a page's own selector (the collection prompt) is applied by rewriting
  // the URL, with no page load and no change hook; announce URL changes so we can look again.
  var URL_CHANGE = 'vehicle-context:urlchange';

  ['pushState', 'replaceState'].forEach(function (method) {
    var original = window.history[method];
    window.history[method] = function () {
      var result = original.apply(this, arguments);
      window.dispatchEvent(new Event(URL_CHANGE));
      return result;
    };
  });

  window.addEventListener('popstate', function () {
    window.dispatchEvent(new Event(URL_CHANGE));
  });

  // Keep the app's own handlers intact while adding ours.
  function onVehicleChange(api, callback) {
    ['onVehicleSelected', 'onVehicleRemoved'].forEach(function (hook) {
      var previous = api[hook];
      api[hook] = function () {
        if (typeof previous === 'function') previous.apply(this, arguments);
        callback();
      };
    });

    var pending = null;
    window.addEventListener(URL_CHANGE, function () {
      window.clearTimeout(pending);
      pending = window.setTimeout(callback, 300);
    });
  }

  function clearVehicle(api) {
    if (typeof api.discardVehicle === 'function') api.discardVehicle();
    window.location.assign(window.location.pathname);
  }

  function collectionNotice() {
    var root = document.querySelector('[data-vehicle-notice]');
    if (!root) return;

    var handle = root.getAttribute('data-collection-handle') || '';
    var title = root.getAttribute('data-collection-title') || '';
    var makes = (root.getAttribute('data-makes') || '')
      .split(',')
      .map(function (make) {
        return make.trim().toLowerCase();
      })
      .filter(Boolean);

    var message = root.querySelector('[data-vehicle-notice-message]');
    var button = root.querySelector('[data-vehicle-notice-clear]');
    var buttonText = button.querySelector('.btn__text') || button;
    var slug = collectionMake(handle, makes);

    whenReady(function (api) {
      function render() {
        if (!hasVehicle(api)) {
          root.hidden = true;
          return;
        }

        var label = vehicleLabel(api);
        if (!label) {
          root.hidden = true;
          return;
        }

        var mismatch = slug && !makesMatch(slug, vehicleMake(api));

        root.hidden = false;
        root.classList.toggle('vehicle-notice--mismatch', !!mismatch);
        message.textContent = mismatch
          ? 'Your saved vehicle (' + label + ') is not part of the ' + title + ' range, so no products are shown.'
          : 'Showing parts that fit your saved vehicle: ' + label + '.';
        buttonText.textContent = 'Show all ' + title;
      }

      button.addEventListener('click', function () {
        clearVehicle(api);
      });

      onVehicleChange(api, render);
      render();
    });
  }

  // The header has one chip in its icons and a second as a phone strip under the search field.
  function vehicleChips() {
    document.querySelectorAll('[data-vehicle-chip]').forEach(vehicleChip);
  }

  // The app's dialog doesn't take focus when it opens; move it to the first field it offers.
  function focusDialog() {
    var tries = 0;
    (function look() {
      var field = document.querySelector('#cm_VehicleWidgetDialog select:not([disabled])');
      if (field) {
        field.focus();
        return;
      }
      tries += 1;
      if (tries < 20) window.setTimeout(look, 50);
    })();
  }

  function vehicleChip(root) {
    var open = root.querySelector('[data-vehicle-chip-open]');
    var label = root.querySelector('[data-vehicle-chip-label]');
    var clear = root.querySelector('[data-vehicle-chip-clear]');
    var emptyLabel = label.textContent.trim();

    whenReady(function (api) {
      function render() {
        var saved = hasVehicle(api) ? vehicleLabel(api) : '';
        root.classList.toggle('vehicle-chip--saved', !!saved);
        label.textContent = saved || emptyLabel;
        open.title = saved;
        clear.hidden = !saved;
      }

      open.addEventListener('click', function () {
        if (typeof api.openVehicleDialog !== 'function') return;
        api.openVehicleDialog();
        focusDialog();
      });

      clear.addEventListener('click', function () {
        if (typeof api.discardVehicle === 'function') api.discardVehicle();
        render();
        open.focus();
      });

      onVehicleChange(api, render);
      render();
      // A vehicle carried in the URL is applied after load without firing the change hooks.
      [500, 1500, 3000].forEach(function (delay) {
        window.setTimeout(render, delay);
      });
    });
  }

  sessionReset();

  function init() {
    collectionNotice();
    vehicleChips();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
